// "How to read this" — an in-app explainer.
//
// The sim encodes a lot in very little ink: terrain colour carries two separate
// variables, and an animal's silhouette carries four. None of that is guessable,
// and a viewer who doesn't know it sees coloured blobs. This is the legend.
//
// Self-contained by design: it builds its own button, its own overlay and its
// own scoped styles (.guide-*), so main.ts only has to call mountGuide(). Colour
// comes from the design tokens in style.css so the legend swatches cannot drift
// away from what the renderer and charts actually draw.

const STYLE_ID = "ecosim-guide-css";

const CSS = `
.guide-btn {
  position: absolute;
  z-index: 12;
  bottom: var(--s-3, 12px);
  /* Bottom-right of the CANVAS column: the scale bar owns the top-right and the
     HUD owns the bottom-left. */
  right: calc(var(--sidebar-w, 360px) + var(--s-3, 12px));
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 11px;
  border: 1px solid var(--line, #333a31);
  border-radius: 999px;
  background: rgba(11, 15, 10, 0.86);
  color: var(--muted, #8c8f84);
  font-family: var(--font-label, system-ui, sans-serif);
  font-size: 11.5px;
  line-height: 1;
  text-transform: uppercase;
  letter-spacing: .13em;
  cursor: pointer;
  backdrop-filter: blur(6px);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.4);
}
.guide-btn:hover { border-color: var(--deer, #d8ab6a); color: var(--deer, #d8ab6a); }
.guide-btn b {
  display: inline-flex; align-items: center; justify-content: center;
  width: 14px; height: 14px; border-radius: 50%;
  border: 1px solid currentColor; color: inherit; background: transparent;
  font-size: 9px; font-weight: 700;
}

.guide-back {
  position: fixed; inset: 0; z-index: 100;
  background: rgba(6, 9, 6, 0.72);
  backdrop-filter: blur(3px);
  display: flex; align-items: center; justify-content: center;
  padding: var(--s-4, 16px);
}
.guide-back[hidden] { display: none; }

.guide {
  width: min(760px, 100%);
  max-height: min(86dvh, 900px);
  overflow-y: auto;
  overscroll-behavior: contain;
  background: var(--panel, #131811);
  border: 1px solid var(--line, #2a3327);
  border-radius: var(--r-lg, 14px);
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.6);
  color: var(--ink, #e8e4d9);
  /* Prose is set in the serif: this panel is the field notebook, and it is
     the one place in the app with sentences rather than readouts. */
  font: 15px/1.62 var(--font-prose, Georgia, serif);
}
.guide-head {
  position: sticky; top: 0;
  display: flex; align-items: baseline; gap: 10px;
  padding: 16px 20px 12px;
  background: linear-gradient(var(--panel, #131811) 78%, transparent);
  border-bottom: 1px solid var(--line, #2a3327);
}
.guide-head h2 {
  margin: 0;
  font-family: var(--font-label, system-ui, sans-serif);
  font-size: 19px; font-weight: 600;
  text-transform: uppercase; letter-spacing: .16em;
}
.guide-head p {
  margin: 0; color: var(--muted, #8c8f84);
  font-family: var(--font-prose, Georgia, serif); font-style: italic; font-size: 13px;
}
.guide-x {
  margin-left: auto; border: 1px solid var(--line, #2a3327); background: transparent;
  color: var(--muted, #8a9580); border-radius: var(--r-sm, 6px);
  padding: 3px 9px; cursor: pointer; font: 12px/1.4 var(--font-ui, system-ui, sans-serif);
}
.guide-x:hover { color: var(--ink, #dde5d4); border-color: var(--muted, #8a9580); }

.guide-body { padding: 4px 20px 22px; }
.guide h3 {
  margin: 24px 0 7px;
  font-family: var(--font-label, system-ui, sans-serif);
  font-size: 13px; font-weight: 600;
  color: var(--deer, #d8ab6a);
  letter-spacing: .18em; text-transform: uppercase;
}
.guide p { margin: 0 0 9px; }
.guide .lede { color: var(--muted, #8a9580); }
.guide b { font-weight: 650; color: #fff; }
.guide code {
  font-family: var(--font-data, ui-monospace, monospace);
  font-size: 12px; color: var(--warn, #e0a458);
}

/* Legend rows: a swatch or glyph, then what it means. */
.guide-rows { display: grid; gap: 8px; margin: 8px 0 14px; }
.guide-row {
  display: grid; grid-template-columns: 62px 1fr; gap: 12px; align-items: center;
  background: var(--panel-2, #1a2017);
  border: 1px solid var(--line, #2a3327);
  border-radius: var(--r-sm, 6px);
  padding: 8px 10px;
}
.guide-row > div:last-child { font-size: 13.5px; }
.guide-key { display: flex; align-items: center; justify-content: center; height: 26px; }
.guide-sw { width: 46px; height: 16px; border-radius: 3px; }

.guide-foot {
  margin-top: 22px; padding-top: 13px;
  border-top: 1px solid var(--line, #333a31);
  color: var(--dim, #5f6357); font-style: italic; font-size: 13px;
}

@media (max-width: 860px) {
  .guide-btn { right: var(--s-3, 12px); bottom: auto; top: var(--s-3, 12px); }
  .guide { max-height: 92dvh; }
  .guide-body { padding: 4px 14px 18px; }
  .guide-row { grid-template-columns: 54px 1fr; gap: 9px; }
}
`;

/** A tiny SVG of an animal as the renderer actually draws it. */
function glyph(fill: string, rx: number, ry: number, rot = 0): string {
  return `<svg width="46" height="22" viewBox="0 0 46 22" aria-hidden="true">
    <ellipse cx="23" cy="11" rx="${rx}" ry="${ry}" fill="${fill}"
      transform="rotate(${rot} 23 11)"/></svg>`;
}

function row(key: string, text: string): string {
  return `<div class="guide-row"><div class="guide-key">${key}</div><div>${text}</div></div>`;
}

function swatch(color: string): string {
  return `<span class="guide-sw" style="background:${color}"></span>`;
}

/** Gradient swatch showing a fertility ramp from grazed-out to full. */
function ramp(from: string, to: string): string {
  return `<span class="guide-sw" style="background:linear-gradient(90deg,${from},${to})"></span>`;
}

const DEER = "var(--deer,#d8ab6a)";
const WOLF = "var(--wolf,#93a6ba)";

function content(): string {
  return `
<h3>What you are watching</h3>
<p class="lede">Animals with a heritable body and a small neural-net brain, living on an
energy budget. Nothing scores them and nothing tells them what to do — whatever stays
fed and survives long enough gets to breed, so any change you see in the population
over time is natural selection actually happening, not a scripted animation.</p>

<h3>The ground</h3>
<p>Terrain colour carries <b>two</b> different things at once, which is why it looks mottled.</p>
<div class="guide-rows">
  ${row(ramp("#4a4326", "#6fbf5c"), "<b>Which colour family</b> is that ground's <b>fertility</b> — a permanent property of the map. Green is rich meadow; olive-brown is naturally thin ground and stays brown even when fully grown.")}
  ${row(ramp("#14180f", "#6fbf5c"), "<b>How bright it is</b> is how <b>grazed</b> it is right now, measured against what that particular cell can hold.")}
</div>
<p>So the two kinds of dark mean opposite things. Large soft brown regions are
<b>poor terrain</b> — geography, not damage. Small hard near-black patches with animals
standing on them are <b>holes a herd has eaten in the map</b>.</p>
<p>That second one is worth watching. The map-wide grass reading can sit above 95%
while the ground directly under a herd is down at 30% — deer cluster, so they run out
of food locally on a map that is almost entirely untouched.</p>

<h3>The animals</h3>
<div class="guide-rows">
  ${row(swatch(DEER), "<b>Deer</b> — warm tan. The same colour identifies them in every chart.")}
  ${row(swatch(WOLF), "<b>Wolves</b> — cool slate blue.")}
  ${row(glyph(DEER, 5, 5) + glyph(DEER, 10, 10), "<b>Size is body mass.</b> A 136 kg stag really is drawn bigger than a 40 kg yearling. <b>Juveniles start small and grow</b> as they mature.")}
  ${row(glyph(DEER, 9, 6), "<b>A compact oval is a bedded animal</b> — legs folded, resting and digesting. Deer spend a large part of the day like this.")}
  ${row(glyph(DEER, 12, 4, -18), "<b>An elongated shape is on the move</b>, pointing where it is going.")}
  ${row(glyph(WOLF, 17, 2.4, -12), "<b>A long thin streak is running.</b> The faster it goes, the more it stretches. Most of the pale streaks crossing open ground are wolves.")}
</div>
<p>Brightness within a species is <b>condition</b>: a starving animal is drawn gaunter and
duller than a well-fed one. Click any animal to open its full genome in the Inspector,
where each gene is a bar showing where that individual sits inside the range its
species allows.</p>

<h3>The charts</h3>
<p><b>Population</b> puts wolves on the right-hand axis on purpose — they run 10–100×
rarer than deer, and on a shared axis they flatten into a line along the floor.</p>
<p><b>The phase portrait</b> is the one worth learning. It plots deer against wolves as a
path rather than against time. A <b>closed loop</b> is a stable cycle; a <b>spiral winding
outward</b> means the system is ringing itself apart and heading for a crash. It shows in
one glance what two separate time series hide.</p>
<p>The <b>evolution</b> charts draw a mean line with a shaded ±1 SD band. The band matters:
a narrow one means the population is converging on an answer, a wide one means it is
splitting into strategies.</p>

<h3>Speed, and the two clocks</h3>
<p>Movement here is <b>literal</b> — one pixel is one metre and, at <code>1×</code>, one second is one
second, so a deer sprinting reads as a real 64 km/h deer.</p>
<p>Life history is <b>not</b>. Ages and lifespans are compressed so a generation takes minutes
instead of years — otherwise you would watch nothing happen for a decade. So <code>1×</code> means
life-like <em>motion</em>, never life-like <em>lifespans</em>. Turn the speed up to watch evolution;
turn it down to watch a hunt.</p>

<h3>Things worth trying</h3>
<p>Use the <b>scenario presets</b> — they keep your seed, so two scenarios run on identical
ground and are directly comparable. <b>Paradise</b> removes the wolves entirely: watch senses
and speed decay over generations once nothing is hunting you, because those traits cost
energy and stop paying for themselves. <b>Predator pressure</b> does the reverse.</p>

<div class="guide-foot">Everything is deterministic: the same seed and settings replay
the same world, animal for animal.</div>`;
}

/**
 * Create the button and overlay, and wire them up. Idempotent — calling twice
 * will not stack duplicates.
 */
export function mountGuide(host: HTMLElement): void {
  if (document.querySelector(".guide-btn")) return;

  if (!document.getElementById(STYLE_ID)) {
    const s = document.createElement("style");
    s.id = STYLE_ID;
    s.textContent = CSS;
    document.head.appendChild(s);
  }

  const btn = document.createElement("button");
  btn.className = "guide-btn";
  btn.type = "button";
  btn.innerHTML = `<b>?</b> How to read this`;

  const back = document.createElement("div");
  back.className = "guide-back";
  back.hidden = true;
  back.innerHTML = `
    <div class="guide" role="dialog" aria-modal="true" aria-label="How to read this">
      <div class="guide-head">
        <h2>How to read this</h2>
        <p>an evolving ecosystem, not an animation</p>
        <button class="guide-x" type="button">Close</button>
      </div>
      <div class="guide-body">${content()}</div>
    </div>`;

  const panel = back.querySelector(".guide") as HTMLElement;
  const close = back.querySelector(".guide-x") as HTMLButtonElement;

  const open = (): void => {
    back.hidden = false;
    panel.scrollTop = 0;
    close.focus();
  };
  const hide = (): void => {
    back.hidden = true;
    btn.focus();
  };

  btn.addEventListener("click", open);
  close.addEventListener("click", hide);
  // Backdrop click closes; a click inside the panel must not.
  back.addEventListener("click", (e) => {
    if (e.target === back) hide();
  });
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && !back.hidden) hide();
  });

  host.appendChild(btn);
  document.body.appendChild(back);
}
